import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import { combineClasses, requiredMessage } from "@/lib/utils";
import { toDateInputValue, toDateTimeInputValue, toOffsetDateTime, toTimeInputValue, todayInputValue } from "@/lib/dateTime";
import { numberRegex, floatingPointNumberRegex } from "@/lib/regex";
import toast from "@/lib/toast";
import routes from "@/routes/routes";
import eventService from "@/services/api/events";
import type { CreateEventPayload, EventType, PublishingStatus } from "@/services/api/events";
import venueService, { type Venue } from "@/services/api/venues";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getChapters } from "@/store/slices/chapterSlice";
import { getEventById } from "@/store/slices/eventSlice";
import Button from "@/components/Button/Button";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import { TableCellTooltip } from "@/components/Tooltip";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import CheckIcon from "@/components/SVGComponents/CheckIcon";
import LocationPinIcon from "@/components/SVGComponents/LocationPinIcon";
import ErrorText from "@/components/ErrorText/ErrorText";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { getEventCityName } from "@/pages/Gatherings/lib/eventFormat";
import { formatINR } from "@/lib/currency";
import TypeSelector from "./components/TypeSelector/TypeSelector";
import type { TypeOption } from "./components/TypeSelector/TypeSelector";
import PublishingOptions from "./components/PublishingOptions/PublishingOptions";
import type { PublishingOption } from "./components/PublishingOptions/PublishingOptions";
import PillToggleGroup from "@/components/PillToggleGroup/PillToggleGroup";
import CoverImageUpload from "./components/CoverImageUpload/CoverImageUpload";
import LocationPickerModal from "./components/LocationPickerModal/LocationPickerModal";
import type { LatLng } from "./components/LocationPickerModal/LocationPickerModal";
import type { CityOption, SelectOption } from "./lib/cityOptions";
import styles from "./CreateGathering.module.scss";
import SettingsService from "@/services/api/settings";

// Parses the `?q=<lat>,<lng>` a `LocationPickerModal` pick produces —
// used to re-center the map on an existing pin when editing a gathering
// that already has one. A pre-picker `mapUrl` (the old hardcoded
// "?q=Taj+Lands+End" place-name placeholder, or any other non-coordinate
// value) simply doesn't match, same as an event with no `mapUrl` at all —
// the field just starts unset rather than guessing at coordinates.
const parseGoogleMapsLatLng = (url: string | null | undefined): LatLng | null => {
  if (!url) return null;
  const match = url.match(/[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

// Real GET /admin/chapters fetch params, same as CitiesChapters.tsx's
// FETCH_PARAMS — "City / chapter" here sources from the same real
// chapters catalog that page uses, per the user, instead of the old
// hardcoded CITY_OPTIONS.
const CHAPTERS_FETCH_PARAMS = { pageNo: 1, perPage: 100 };

// Cover image upload cap: 5MB
const MAX_COVER_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

// Hardcoded — no gatherings API/slice exists yet (see docs/architecture.md).
// Replace with real option catalogs once one is wired up.
const TYPE_OPTIONS: TypeOption[] = [
  { value: "the_marquee", label: "The Marquee", caption: "Grand house gathering" },
  { value: "micro_meetup", label: "Micro-meetup", caption: "8–16 members" },
  { value: "masterclass", label: "Masterclass", caption: "Led by a member" },
];

// Maps this page's type options to the real POST /events `eventType` enum
// — all three values confirmed via a real GET /events response (see
// services/api/events.ts; "MICRO_MEETUPS" is plural).
const EVENT_TYPE_MAP: Record<string, EventType> = {
  the_marquee: "MARQUEE",
  micro_meetup: "MICRO_MEETUPS",
  masterclass: "MASTERCLASS",
};

// Reverse of EVENT_TYPE_MAP — used to prefill the type selector when
// editing an existing gathering (see the edit-prefill effect below).
const EVENT_TYPE_TO_UI: Record<EventType, string> = {
  MARQUEE: "the_marquee",
  MICRO_MEETUPS: "micro_meetup",
  MASTERCLASS: "masterclass",
};

const PUBLISHING_OPTIONS: PublishingOption[] = [
  { value: "publish_now", label: "Publish now", caption: "Visible to eligible members immediately" },
  { value: "schedule", label: "Schedule announcement", caption: "Pick the date and time it goes live" },
  { value: "draft", label: "Keep as draft", caption: "Only visible to the admin team" },
];

const ROOM_OPTIONS = [
  { value: "charter", label: "Chartered" },
  { value: "uncharter", label: "Unchartered" },
];

// example body, "UNCHARTERED" from a real GET /events response sample
// (event id 87 carries `["CHARTERED", "UNCHARTERED"]` — both at once,
// which is exactly why Room is a multi-select, per the user) — see
// services/api/events.ts and docs/decisions.md.
const ROOM_TO_CATEGORY: Record<string, string> = {
  charter: "CHARTERED",
  uncharter: "UNCHARTERED",
};

// Maps the Publishing sidebar's selection to the real `publishingStatus`
// enum. "PUBLISHED" and "DRAFT" are confirmed; "SCHEDULED" is inferred
// (see the PublishingStatus comment in services/api/events.ts).
const PUBLISHING_STATUS_MAP: Record<string, PublishingStatus> = {
  publish_now: "PUBLISHED",
  schedule: "SCHEDULED",
  draft: "DRAFT",
};

// Reverse of PUBLISHING_STATUS_MAP, for edit-prefill — `null`/unrecognized
// falls back to "draft", same as `getPublishingLabel` in
// pages/Gatherings/lib/eventFormat.ts already treats a `null`
// publishingStatus as "not yet published".
const PUBLISHING_STATUS_TO_UI: Record<string, string> = {
  PUBLISHED: "publish_now",
  SCHEDULED: "schedule",
  DRAFT: "draft",
};

const basicsConfig: FormConfig = {
  title: {
    controlType: "input",
    label: "Gathering title",
    isRequired: true,
    labelVariant: "caption",
    validations: {
      required: requiredMessage("Gathering title"),
      maxLength: { value: 120, message: "Gathering title must be under 120 characters" },
    },
  },
  oneLineDescription: {
    controlType: "input",
    label: "One-line description (shown on the home card)",
    isRequired: true,
    labelVariant: "caption",
    validations: {
      required: requiredMessage("One-line description"),
      maxLength: { value: 500, message: "One-line description must be under 500 characters" },
    },
  },
};

const whenRow1Config: FormConfig = {
  date: {
    controlType: "date",
    label: "Date",
    labelVariant: "caption",
    isRequired: true,
    isControlled: true,
    validations: { required: requiredMessage("Date") },
    props: { min: todayInputValue() },
  },
  startTime: {
    controlType: "time",
    label: "Start",
    labelVariant: "caption",
    isRequired: true,
    isControlled: true,
    validations: { required: requiredMessage("Start time") },
  },
  // Swapped in for "End time" per the user — real working POST /events
  // example body confirmed `guestArrivesBy` is a real, expected field
  // (sent as an OffsetDateTime on the event's date); removed from this UI in
  // decision #34 before that body was seen. See docs/decisions.md.
  guestArrivesBy: {
    controlType: "time",
    label: "Guests arrive by",
    isRequired: true,
    labelVariant: "caption",
    isControlled: true,
    validations: { required: requiredMessage("Guests arrive by") },
  },
};

const eveningRow1Config: FormConfig = {
  dressCode: { controlType: "input", label: "Dress code", labelVariant: "caption" },
  hostedBy: {
    controlType: "input",
    label: "Hosted by",
    labelVariant: "caption",
    isControlled: true,
  },
};

const descriptionConfig: FormConfig = {
  description: {
    controlType: "textarea",
    label: "Description shown on the gathering page",
    labelVariant: "caption",
    validations: {
      maxLength: { value: 1000, message: "Description must be under 1000 characters" },
    },
  },
};



const rsvpConfig: FormConfig = {
  rsvpCloses: {
    controlType: "datetime",
    label: "RSVP closed date",
    isRequired: true,
    labelVariant: "caption",
    isControlled: true,
    validations: {
      required: requiredMessage("RSVP closed date"),
      validate: (value: string, formValues: CreateGatheringFormValues) => {
        if (!formValues.date || !formValues.startTime) return true;
        const eventStart = new Date(`${formValues.date}T${formValues.startTime}`);
        const rsvpCloseDate = new Date(value);
        if (Number.isNaN(eventStart.getTime()) || Number.isNaN(rsvpCloseDate.getTime())) return true;
        return rsvpCloseDate < eventStart || "RSVP must close before the gathering starts";
      },
    },
    props: { min: todayInputValue() },
  },
};

interface CreateGatheringFormValues {
  title: string;
  oneLineDescription: string;
  date: string;
  startTime: string;
  guestArrivesBy: string;
  venue: SelectOption | null;
  city: CityOption | null;
  // A picked "https://maps.google.com/?q=<lat>,<lng>". See
  // LocationPickerModal.tsx and docs/decisions.md #83.
  mapUrl: string;
  dressCode: string;
  hostedBy: string;
  description: string;
  ticketPrice: string;
  capacity: string;
  // "YYYY-MM-DDTHH:mm" from the DateTime FormControl (components/FormControl/
  // DateTime) — not a SelectOption like the other controlled fields, since
  // this one has no discrete option list to pick from.
  rsvpCloses: string;
  coverImage?: string;
  scheduledPublishAt: string;
}

const CreateGathering = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { eventId } = useParams<{ eventId?: string }>();
  const isEditing = Boolean(eventId);
  const [gstRate, setGstRate] = useState<number>(18);
  const [type, setType] = useState<string>(TYPE_OPTIONS[0]?.value ?? "");
  const [publishing, setPublishing] = useState<string>("draft");
  const [room, setRoom] = useState<string[]>(["charter"]);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  // "Map location" — holds the resulting "https://maps.google.com/?q=<lat>,
  // <lng>" string as `mapUrl`, per the user. `pickedLocation` is separate,
  // real-number state so `LocationPickerModal` can re-center its map on
  // the current pin without having to re-parse the URL on every render.
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [pickedLocation, setPickedLocation] = useState<LatLng | null>(null);
  // The city the current pin was verified against in LocationPickerModal —
  // switching "City / chapter" afterwards clears the now-mismatched pin.
  const [pinnedCityId, setPinnedCityId] = useState<number | null>(null);

  // Real chapters for "City / chapter" — given directly by the user,
  // same GET /admin/chapters CitiesChapters.tsx uses.
  const chapters = useAppSelector((state) => state.chapter.chapters);
  const chapterOptions: CityOption[] = useMemo(
    () => chapters.map((chapter) => ({ label: chapter.name, value: String(chapter.id), id: chapter.id })),
    [chapters],
  );

  useEffect(() => {
    dispatch(getChapters(CHAPTERS_FETCH_PARAMS));
  }, [dispatch]);

  // The gathering being edited — a real `GET /events/{eventId}` fetch
  // (`getEventById`), not derived from the `GET /events` list already in
  // Redux: that list's own items aren't confirmed to carry `locations`
  // (see its comment in services/api/events.ts), which is what made the
  // "City / chapter" prefill below silently fail to match anything.
  const editingEvent = useAppSelector((state) => state.event.editingEvent);
  const [hasFetchedEditingEvent, setHasFetchedEditingEvent] = useState(false);
  
  useEffect(() => {
  const fetchGstRate = async () => {
    try {
      const data = await SettingsService.getPaymentTaxSettings();

     setGstRate(data.taxPercentage ?? 18);
    } catch {
      console.log('error');
    }
  };
  fetchGstRate();
}, []);

  useEffect(() => {
    setHasFetchedEditingEvent(false);
    if (!isEditing || !eventId) return;
    let cancelled = false;
    dispatch(getEventById(Number(eventId))).finally(() => {
      if (!cancelled) setHasFetchedEditingEvent(true);
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, isEditing, eventId]);

  const { register, handleSubmit, control, watch, setValue, reset, formState, clearErrors } = useForm<CreateGatheringFormValues>({
    defaultValues: {
      title: "",
      oneLineDescription: "",
      date: "",
      startTime: "",
      guestArrivesBy: "",
      venue: null,
      city: null,
      mapUrl: "",
      dressCode: "",
      hostedBy: "",
      description: "",
      ticketPrice: "",
      capacity: "",
      rsvpCloses: "",
      coverImage: "",
      scheduledPublishAt: "",
    },
  });

  const title = watch("title");
  const city = watch("city");
  const ticketPrice = watch("ticketPrice");
  const mapUrl = watch("mapUrl");
  useEffect(() => {
    if (pinnedCityId === null || city?.id === pinnedCityId) return;
    setValue("mapUrl", "");
    setPickedLocation(null);
    setPinnedCityId(null);
  }, [city?.id, pinnedCityId, setValue]);

  const isTicketPriceDisabled =room.length === 1 && room[0] === "charter";

  useEffect(() => {
    if (isTicketPriceDisabled) {
      setValue("ticketPrice", "", { shouldValidate: true });
      clearErrors("ticketPrice");
    }
  }, [isTicketPriceDisabled, setValue, clearErrors]);

  const ticketingConfig: FormConfig = {
    ticketPrice: {
      controlType: "input",
      label: "Ticket price",
      labelVariant: "caption",
      isDisabled: isTicketPriceDisabled,
      props: { type: "number", placeholder: "0", min: 0, disabled: isTicketPriceDisabled },
      validations: {
        validate: (value: string) => {
          if (isTicketPriceDisabled) return true;
          if (!value) return true;
          if (!floatingPointNumberRegex.test(value)) return "Enter a valid amount";
          return Number(value) >= 0 || "Ticket price can't be negative";
        },
      },
    },
  capacity: {
    controlType: "input",
    label: "Capacity",
    isRequired: true,
    labelVariant: "caption",
    props: { type: "number", placeholder: "0", min: 1 },
    validations: {
      required: requiredMessage("Capacity"),
      pattern: { value: numberRegex, message: "Capacity must be a whole number" },
      min: { value: 1, message: "Capacity must be at least 1" },
    },
  },
};
  // Chapters load asynchronously — default "City / chapter" to the first
  // one once available, same default-to-first behavior the old
  // `CITY_OPTIONS[0]` default gave synchronously. Skipped entirely while
  // editing — the edit-prefill effect below sets the real matching
  // chapter instead, and this would otherwise briefly flash the wrong one
  // while `editingEvent` is still loading.
  useEffect(() => {
    if (!isEditing && !city && chapterOptions.length > 0) {
      setValue("city", chapterOptions[0] ?? null);
    }
  }, [isEditing, city, chapterOptions, setValue]);

  // Real venues for the selected chapter — given directly by the user:
  // GET /admin/venues/chapter/{chapterId}. Refetched whenever "City /
  // chapter" changes; the previously selected venue is cleared since it
  // may not belong to the new chapter.
  const [chapterVenues, setChapterVenues] = useState<Venue[]>([]);
  const [isLoadingVenues, setIsLoadingVenues] = useState(false);

  // On top of the chapter filter above, only venues whose `suitableFor`
  // includes the selected gathering type are offered.
  const selectedEventType = EVENT_TYPE_MAP[type];
  const venueOptions: SelectOption[] = useMemo(
    () =>
      chapterVenues
        .filter((venue) => !!selectedEventType && (venue.suitableFor ?? []).includes(selectedEventType))
        .map((venue) => ({ label: venue.name, value: venue.name })),
    [chapterVenues, selectedEventType],
  );

  // Clears the picked venue once it's no longer offered (e.g. the type
  // changed to one the venue isn't suitable for).
  const selectedVenue = watch("venue");
  useEffect(() => {
    if (selectedVenue && !venueOptions.some((option) => option.value === selectedVenue.value)) {
      setValue("venue", null);
    }
  }, [selectedVenue, venueOptions, setValue]);

  useEffect(() => {
    setValue("venue", null);

    if (!city?.id) {
      setChapterVenues([]);
      return;
    }

    let cancelled = false;
    setIsLoadingVenues(true);

    venueService
      .getVenuesByChapter(city.id)
      .then((res) => {
        if (cancelled) return;
        const venues = res.data?.results ?? [];
        setChapterVenues(venues);
        // Matched against every chapter venue; the effect above clears it
        // again if it isn't suitable for the event's type.
        const options = venues.map((venue) => ({ label: venue.name, value: venue.name }));

        // Edit-prefill: `editingEvent.locations[0].name` (this event's real
        // venue) can only be matched against `venueOptions` once this
        // fetch — triggered by the `city` prefill above — resolves, unlike
        // `city` itself which matches synchronously against
        // `chapterOptions`. Matched case/whitespace-insensitively, same
        // convention as the "City / chapter" match.
        if (isEditing && editingEvent && String(editingEvent.id) === eventId) {
          const editedVenueName = editingEvent.locations?.[0]?.name;
          const match = editedVenueName
            ? options.find((option) => option.label.trim().toLowerCase() === editedVenueName.trim().toLowerCase())
            : undefined;
          if (match) setValue("venue", match);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingVenues(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city?.id]);

  // Applies once per edited gathering — `appliedEditRef` stops this from
  // re-running (and clobbering the admin's own in-progress edits) on
  // every render, but resets if `eventId` itself changes (e.g. navigating
  // from editing one gathering straight to another without a full
  // remount).
  const appliedEditRef = useRef(false);

  useEffect(() => {
    appliedEditRef.current = false;
  }, [eventId]);

  useEffect(() => {
    // The `String(editingEvent.id) === eventId` check guards against a
    // previous gathering's still-in-flight-replacement data (belt and
    // braces alongside eventSlice's own `getEventById.pending` clear) —
    // without it, this could fire once using the *wrong* gathering's data
    // and never get a second chance to fire again correctly, since
    // `appliedEditRef` only allows one run per mount.
    if (
      !isEditing ||
      !hasFetchedEditingEvent ||
      !editingEvent ||
      String(editingEvent.id) !== eventId ||
      appliedEditRef.current ||
      chapterOptions.length === 0
    )
      return;
    appliedEditRef.current = true;

    setType(EVENT_TYPE_TO_UI[editingEvent.eventType] ?? TYPE_OPTIONS[0]?.value ?? "");
    setPublishing(PUBLISHING_STATUS_TO_UI[editingEvent.publishingStatus ?? ""] ?? "draft");
    const prefilledRoom = Object.keys(ROOM_TO_CATEGORY).filter((key) =>
      editingEvent.visibleToCategories?.includes(ROOM_TO_CATEGORY[key] ?? ""),
    );
    setRoom(prefilledRoom.length > 0 ? prefilledRoom : ["charter"]);

    reset({
      title: editingEvent.name,
      oneLineDescription: editingEvent.description,
      date: toDateInputValue(editingEvent.eventDate),
      startTime: toTimeInputValue(editingEvent.eventStartTime),
      guestArrivesBy: editingEvent.guestArrivesBy ? toTimeInputValue(editingEvent.guestArrivesBy) : "",
      // `GET /events/{id}` has no dedicated `venue` field, but its
      // `locations[0]` carries the same venue picked on create — `name`
      // maps to this page's `venue` field per a real sample response given
      // directly by the user. Left blank here (rather than set directly):
      // the venue-fetch effect below clears + repopulates this field the
      // moment `city` changes above, so the actual prefill match happens
      // there once `venueOptions` for the matched chapter has loaded.
      venue: null,
      // `option.label` (a chapter's own `name`) and `getEventCityName`'s
      // `cityName` are two independently free-text fields (one entered
      // when the chapter was created, one from wherever the event's
      // location was picked) — matched case/whitespace-insensitively
      // since an exact `===` would silently fail to preselect anything on
      // something as easy to happen as "Mumbai " vs "Mumbai" or "mumbai".
      city:
        chapterOptions.find(
          (option) => option.label.trim().toLowerCase() === getEventCityName(editingEvent)?.trim().toLowerCase(),
        ) ?? null,
      // Real GET /events does return `mapUrl` directly (unrelated to
      // `locations`), so an existing pin (or pre-picker place-name
      // placeholder, or nothing at all) prefills correctly.
      mapUrl: editingEvent.mapUrl ?? "",
      dressCode: editingEvent.dressCode ?? "",
      hostedBy: editingEvent.hostedBy ?? "",
      description: editingEvent.guidelines ?? "",
      ticketPrice: editingEvent.ticketPrice ? String(editingEvent.ticketPrice) : "",
      capacity: String(editingEvent.capacity ?? ""),
      rsvpCloses: editingEvent.rsvpClosesOn ? toDateTimeInputValue(editingEvent.rsvpClosesOn) : "",
      coverImage: editingEvent.thumbnailUrl ?? "",
      scheduledPublishAt: editingEvent.scheduledPublishAt ? toDateTimeInputValue(editingEvent.scheduledPublishAt) : "",
    });
    setPickedLocation(parseGoogleMapsLatLng(editingEvent.mapUrl));
  }, [isEditing, hasFetchedEditingEvent, editingEvent, eventId, chapterOptions, reset]);

  const whenRow2Config: FormConfig = {
    city: {
      controlType: "select",
      label: "City / chapter",
      labelVariant: "caption",
      isControlled: true,
      // A gathering's chapter can't be changed once it exists.
      isDisabled: isEditing,
      validations: { required: requiredMessage("City / chapter") },
      props: { options: chapterOptions, isSearchable: false },
    },
    venue: {
      controlType: "select",
      label: "Venue",
      labelVariant: "caption",
      isRequired: true,
      isControlled: true,
      validations: { required: requiredMessage("Venue") },
      props: {
        options: venueOptions,
        isSearchable: false,
        isLoading: isLoadingVenues,
        noOptionsMessage: () => (city ? "No venues for this chapter" : "Pick a city / chapter first"),
      },
    },
  };

  const scheduledPublishAtConfig: FormConfig = {
    scheduledPublishAt: {
      controlType: "datetime",
      label: "Announcement date & time",
      isRequired: true,
      labelVariant: "caption",
      isControlled: true,
      validations: {
        required: requiredMessage("Announcement date & time"),
        validate: (value: string, formValues: CreateGatheringFormValues) => {
          if (!formValues.date || !formValues.startTime) return true;
          const eventStart = new Date(`${formValues.date}T${formValues.startTime}`);
          const announcementDate = new Date(value);
          if (Number.isNaN(eventStart.getTime()) || Number.isNaN(announcementDate.getTime())) return true;
          return announcementDate < eventStart || "Announcement must be before the gathering starts";
        },
      },
      props: { min: todayInputValue() },
    },
  };

  const typeLabel = TYPE_OPTIONS.find((option) => option.value === type)?.label.replace(/^The /, "") ?? "Gathering";
  usePageSubtitle(
    isEditing
      ? `Editing ${typeLabel} · ${city?.label ?? "—"}`
      : `New ${typeLabel} · ${city?.label ?? "—"} · draft`,
  );

  const priceNumber = Number(ticketPrice) || 0;
  const gstAmount = Math.round(priceNumber * (gstRate / 100));
  const memberPays = priceNumber + gstAmount;

  const goToGatherings = () => navigate(routes.privateRoutes.GATHERINGS.path);

  const handleThumbnailFileChange = (selectedFile: File | null) => {
    if (selectedFile && selectedFile.size > MAX_COVER_IMAGE_SIZE_BYTES) {
      toast.error("Cover image must be under 5MB.");
      return;
    }
    setThumbnailFile(selectedFile);
    const hasImage = Boolean(selectedFile || (isEditing && editingEvent?.thumbnailUrl));
    if (hasImage) {
      clearErrors("coverImage");
      setValue("coverImage", selectedFile ? selectedFile.name : (editingEvent?.thumbnailUrl ?? ""), { shouldValidate: true });
    } else {
      setValue("coverImage", "", { shouldValidate: true });
    }
  };
  const buildEventPayload = (values: CreateGatheringFormValues, status: PublishingStatus): CreateEventPayload => {
    // Both fields carry the same instant — the selected date at the
    // selected start time, in the admin's local offset.
    const eventStart = toOffsetDateTime(`${values.date}T${values.startTime}`) ?? "";
    const rsvpClosesOn = values.rsvpCloses ? toOffsetDateTime(values.rsvpCloses) : null;
    return {
      name: values.title,
      eventType: EVENT_TYPE_MAP[type] ?? "MARQUEE",
      description: values.oneLineDescription,
      eventDate: eventStart,
      eventStartTime: eventStart,
      guestArrivesBy: toOffsetDateTime(`${values.date}T${values.guestArrivesBy}`) ?? "",
      capacity: Number(values.capacity) || 0,
      publishingStatus: status,
      visibleToCategories: room
        .map((r) => ROOM_TO_CATEGORY[r])
        .filter((category): category is string => Boolean(category))
        .join(","),
      mapUrl: values.mapUrl,
      scheduledPublishAt:
        publishing === "schedule" && values.scheduledPublishAt ? toOffsetDateTime(values.scheduledPublishAt) : null,
      ...(values.description ? { guidelines: values.description } : {}),
      ...(values.venue ? { venue: values.venue.value } : {}),
      ...(values.dressCode ? { dressCode: values.dressCode } : {}),
      ...(values.hostedBy ? { hostedBy: values.hostedBy } : {}),
      ...(values.ticketPrice ? { ticketPrice: Number(values.ticketPrice) || 0 } : {}),
      ...(values.city ? { cityId: values.city.id } : {}),
      ...(rsvpClosesOn ? { rsvpClosesOn } : {}),
    };
  };

  // isEditing → eventService.updateEvent (same PUT /admin/events endpoint
  // as createEvent, with a real `eventId` added — see the NOT-CONFIRMED
  // caveat on UpdateEventPayload in services/api/events.ts). The selected
  // cover image (if any) goes along in this same call as a `file` part —
  // no separate upload request anymore, per the user.
  const saveEvent = (values: CreateGatheringFormValues, status: PublishingStatus) => {
    const payload = buildEventPayload(values, status);
    const targetEventId = editingEvent?.id ?? (eventId ? Number(eventId) : undefined);
    return isEditing && targetEventId
      ? eventService.updateEvent({ ...payload, eventId: targetEventId, gatheringId: targetEventId }, thumbnailFile ?? undefined)
      : eventService.createEvent(payload, thumbnailFile ?? undefined);
  };

  const onSaveDraft = handleSubmit(async (values) => {
    try {
      await saveEvent(values, "DRAFT");
      toast.success(isEditing ? "Draft updated." : "Draft saved.");
      navigate(routes.privateRoutes.GATHERINGS.path);
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  });

  const onPublish = handleSubmit(async (values) => {
    try {
      await saveEvent(values, PUBLISHING_STATUS_MAP[publishing] ?? "PUBLISHED");
      toast.success(isEditing ? "Gathering updated successfully." : "Gathering created successfully.");
      navigate(routes.privateRoutes.GATHERINGS.path);
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  });

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <button type="button" className={styles.backLink} onClick={goToGatherings}>
          <ArrowIcon className={styles.backIcon} />
        </button>

        <div className={styles.toolbarTitleBlock}>
          <div className={styles.toolbarTitleRow}>
            <TableCellTooltip
              value={title || (isEditing ? "Loading gathering…" : "New gathering")}
              textClassName={styles.toolbarTitle}
            />
            <StatusBadge
              label={PUBLISHING_STATUS_MAP[publishing] === "PUBLISHED" ? "PUBLISHED" : "DRAFT"} tone={PUBLISHING_STATUS_MAP[publishing] === "PUBLISHED" ? "success" : "neutral"}
            />
          </div>
          <span className={styles.savedLabel}>{isEditing ? "Editing" : "Not saved yet"}</span>
        </div>

        <div className={styles.toolbarActions}>
          <Button outlined className={styles.toolbarButton} onClick={onSaveDraft} disabled={formState.isSubmitting}>
            SAVE DRAFT
          </Button>
          <Button
            startIcon={<CheckIcon />}
            className={styles.toolbarButton}
            onClick={onPublish}
            disabled={formState.isSubmitting}
          >
            {isEditing ? "SAVE CHANGES" : "PUBLISH GATHERING"}
          </Button>
        </div>
      </div>

      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>The Basics</h2>
            <TypeSelector options={TYPE_OPTIONS} value={type} onChange={setType} disabled={formState.isSubmitting} />
            <div className={styles.stackedFields}>
              {formRenderer<CreateGatheringFormValues>(basicsConfig, formState, register, control)}
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>When & Where</h2>
            <div className={styles.row3}>
              {formRenderer<CreateGatheringFormValues>(whenRow1Config, formState, register, control)}
            </div>
            <div className={styles.row2}>
              {formRenderer<CreateGatheringFormValues>(whenRow2Config, formState, register, control)}
            </div>

            <div className={styles.stackedFields}>
              <div className={styles.mapField}>
                <label className={styles.mapFieldLabel} htmlFor="mapUrl">
                  Map location <span className={styles.requiredAsterisk}>*</span>
                </label>
                <input type="hidden" id="mapUrl" {...register("mapUrl", { required: requiredMessage("Map location") })} />
                <button
                  type="button"
                  disabled={formState.isSubmitting || !city}
                  className={combineClasses(styles.mapFieldButton, mapUrl && styles.mapFieldButtonFilled)}
                  onClick={() => setIsLocationModalOpen(true)}
                >
                  <LocationPinIcon width={14} height={14} className={styles.mapFieldIcon} />
                  <span className={combineClasses(styles.mapFieldValue, !mapUrl && styles.mapFieldValueEmpty)}>
                    {mapUrl || (city ? "Click to pick a location on the map" : "Pick a city / chapter first")}
                  </span>
                </button>
                {formState.errors.mapUrl && <ErrorText>{String(formState.errors.mapUrl.message)}</ErrorText>}
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>The Evening</h2>
            <div className={styles.row2}>
              {formRenderer<CreateGatheringFormValues>(eveningRow1Config, formState, register, control)}
            </div>
            {formRenderer<CreateGatheringFormValues>(descriptionConfig, formState, register, control)}
            {/* Hidden input to bind CoverImageUpload to react-hook-form validation */}
            <input
              type="hidden"
              {...register("coverImage", {
                validate: (value) => Boolean(value) || "Cover image is required",
              })}
            />
            {/* A real upload — sent as a `file` part alongside the rest of the payload in the same create/edit request, see `saveEvent` below. */}
            <CoverImageUpload
              file={thumbnailFile}
              onFileChange={handleThumbnailFileChange}
              disabled={formState.isSubmitting}
              error={formState.errors.coverImage?.message}
              existingImageUrl={editingEvent?.thumbnailUrl}
            />
          </div>
        </div>

        <div className={styles.sideColumn}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Publishing</h2>
            <PublishingOptions
              options={PUBLISHING_OPTIONS}
              value={publishing}
              onChange={setPublishing}
              disabled={formState.isSubmitting}
            />
            {publishing === "schedule" && (
              <div className={styles.stackedFields}>
                {formRenderer<CreateGatheringFormValues>(scheduledPublishAtConfig, formState, register, control)}
              </div>
            )}
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Ticketing</h2>
              {/* <StatusBadge label={priceNumber > 0 ? "PAID" : "FREE"} tone="brand" /> */}
            </div>
            <div className={styles.row2}>
              {formRenderer<CreateGatheringFormValues>(ticketingConfig, formState, register, control)}
            </div>
            {priceNumber > 0 && (
              <div className={styles.ticketSummary}>
                <div className={styles.summaryRow}>
                  <span>Ticket</span>
                  <span>{formatINR(priceNumber)}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>GST {gstRate}%</span>
                  <span>{formatINR(gstAmount)}</span>
                </div>
                <div className={combineClasses(styles.summaryRow, styles.summaryRowBold)}>
                  <span>Member pays</span>
                  <span>{formatINR(memberPays)}</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Who Can See This</h2>
            <div className={styles.audienceGroup}>
              <span className={styles.audienceLabel}>Room</span>
              <PillToggleGroup options={ROOM_OPTIONS} value={room} onChange={setRoom} disabled={formState.isSubmitting} />
            </div>
            {formRenderer<CreateGatheringFormValues>(rsvpConfig, formState, register, control)}
            {/* <div className={styles.audienceNote}>
              <UsersIcon className={styles.audienceNoteIcon} />
              412 members will see this gathering
            </div> */}
          </div>
        </div>
      </div>

      <LocationPickerModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        initialValue={pickedLocation}
        cityName={city?.label}
        onConfirm={(result) => {
          setValue("mapUrl", result.url, { shouldValidate: true });
          setPickedLocation({ lat: result.lat, lng: result.lng });
          setPinnedCityId(city?.id ?? null);
          setIsLocationModalOpen(false);
        }}
      />
    </div>
  );
};

export default CreateGathering;
